import { useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { map } from '../core/MapView';
import useMapControls from './useMapControls';
import { useTranslation } from '../../common/components/LocalizationProvider';
import './mapControls.css';

/**
 * Controle customizado para MapLibre GL
 * Renderiza um painel vertical com toggles
 */
class MapControlsPanelControl {
  constructor(container) {
    this.container = container;
  }

  onAdd() {
    return this.container;
  }

  onRemove() {
    this.container.parentNode?.removeChild(this.container);
  }
}

/**
 * Painel de controles do mapa estilo GPSWox
 */
const MapControlsPanel = () => {
  const theme = useTheme();
  const t = useTranslation();
  const {
    settings, toggleSetting, fitBounds, availability, filterPanels, toggleFilterPanel,
  } = useMapControls();

  // Container para o controle do mapa
  const controlContainer = useMemo(() => {
    const container = document.createElement('div');
    container.className = 'maplibregl-ctrl maplibregl-ctrl-group map-controls-panel';
    return container;
  }, []);

  // Controle do mapa
  const control = useMemo(
    () => new MapControlsPanelControl(controlContainer),
    [controlContainer]
  );

  // Adiciona/remove controle do mapa
  useEffect(() => {
    map.addControl(control, theme.direction === 'rtl' ? 'top-left' : 'top-right');
    return () => {
      try {
        map.removeControl(control);
      } catch {
        // Ignora erro se o mapa já foi destruído
      }
    };
  }, [control, theme.direction]);

  // Handler para fit bounds
  const handleFitBounds = useCallback(() => {
    // Remove foco do botão para evitar warning de aria-hidden
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    fitBounds();
  }, [fitBounds]);

  // Handler genérico para toggles
  const handleToggle = useCallback((key) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    toggleSetting(key);
  }, [toggleSetting]);

  // Handler para botões de filtro
  const handleFilterToggle = useCallback((filterType) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    toggleFilterPanel(filterType);
  }, [toggleFilterPanel]);

  // Definição dos controles - memoizado para evitar re-renders
  const controls = useMemo(() => [
    {
      id: 'clustering',
      icon: 'ctrl-clustering',
      title: t('mapClustering') || 'Agrupamento',
      setting: 'clustering',
    },
    {
      id: 'fitBounds',
      icon: 'ctrl-fit-bounds',
      title: t('mapFitBounds') || 'Centralizar Todos',
      isAction: true,
    },
    {
      id: 'showDevices',
      icon: 'ctrl-devices',
      title: t('deviceTitle') || 'Dispositivos',
      setting: 'showDevices',
    },
    {
      id: 'showGeofences',
      icon: 'ctrl-geofences',
      title: t('sharedGeofences') || 'Geofences',
      setting: 'showGeofences',
    },
    {
      id: 'filterGeofence',
      icon: 'ctrl-filter-geofence',
      title: t('mapFilterGeofences') || 'Filtrar Geofences',
      isFilter: 'geofence',
    },
    {
      id: 'showRoutes',
      icon: 'ctrl-routes',
      title: t('reportRoute') || 'Rotas',
      setting: 'showRoutes',
      disabled: true, // TODO: implementar rotas salvas
    },
    {
      id: 'showPoi',
      icon: 'ctrl-poi',
      title: t('mapPoiLayer') || 'Pontos de Interesse',
      setting: 'showPoi',
      checkAvailability: 'showPoi',
    },
    {
      id: 'filterPoi',
      icon: 'ctrl-filter-poi',
      title: t('poiFilterGroups') || 'Filtrar POIs',
      isFilter: 'poi',
    },
    {
      id: 'showLabels',
      icon: 'ctrl-labels',
      title: t('mapShowLabels') || 'Mostrar Nomes',
      setting: 'showLabels',
    },
    {
      id: 'showTail',
      icon: 'ctrl-tail',
      title: t('mapLiveRoutes') || 'Rastro',
      setting: 'showTail',
    },
    {
      id: 'showTraffic',
      icon: 'ctrl-traffic',
      title: t('mapGoogleTraffic') || 'Tráfego ao Vivo',
      setting: 'showTraffic',
      checkAvailability: 'showTraffic',
    },
  ], [t]); // Só recria quando traduções mudam

  // Renderiza os botões no container do controle
  useEffect(() => {
    // Limpa container
    controlContainer.innerHTML = '';

    // Renderiza cada botão
    controls.forEach((ctrl) => {
      // Verifica disponibilidade usando o mapa de availability
      if (ctrl.checkAvailability && !availability[ctrl.checkAvailability]) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `maplibregl-ctrl-icon map-control-btn ${ctrl.icon}`;
      button.title = ctrl.title;
      button.setAttribute('aria-label', ctrl.title);

      if (ctrl.disabled) {
        button.classList.add('disabled');
        button.disabled = true;
      }

      if (ctrl.isAction) {
        // Botão de ação (não é toggle)
        button.onclick = handleFitBounds;
      } else if (ctrl.isFilter) {
        // Botão de filtro (abre/fecha painel)
        const isActive = filterPanels[ctrl.isFilter];
        if (isActive) {
          button.classList.add('active');
        }
        button.setAttribute('aria-pressed', String(isActive));
        button.onclick = () => handleFilterToggle(ctrl.isFilter);
      } else {
        // Botão toggle
        const isActive = settings[ctrl.setting];
        if (isActive) {
          button.classList.add('active');
        }
        button.setAttribute('aria-pressed', String(isActive));
        button.onclick = () => handleToggle(ctrl.setting);
      }

      controlContainer.appendChild(button);
    });
  }, [settings, availability, controls, controlContainer, handleToggle, handleFitBounds, filterPanels, handleFilterToggle]);

  return null;
};

export default MapControlsPanel;
